// Order summary aside for Build an order.
//
// W32 (2026-10-20): the customer line and address recipient come from
// the `customer` prop (SARAH in preview, the real HubSpot profile on a
// real session) instead of the fixture import. In `mode="real"` the
// shipping block renders the HubSpot address (or an honest loading /
// none / error line), and the payment card shows "No saved card on file
// in this review".
//
// W36 (2026-10-20): in `mode="real"` every number is the pricing
// engine's: basket lines, the tier line, the subscription 20% line, the
// bundle line and the products subtotal come from `engine` (the catalog
// and basket quotes of POST /v1/pricing/calculate). Tax and the order
// total read "Pending Shopify tax" until a draft-order calculate is
// wired; "Process order" stays a guarded no-charge control behind
// LIVE_WRITE_GATE_SHOPIFY_CREATE_DRAFT_ORDER; Change opens the payment
// dialog shell once the engine has quoted the catalog. While the engine
// is unreachable the price block keeps the W32 "Prices pending the
// pricing engine" line. Preview output still uses the example table
// passed in through `examplePrices`.
import type { ReactNode } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import {
  BUNDLE_DISCOUNT_PREFIX,
  SUBSCRIPTION_DISCOUNT_SOURCE,
  formatCents,
  tierLabel,
  tierSourceLabel,
  type EngineLine,
  type EngineQuote,
} from '../../../data/livePricing.js';
import type { CatalogEntry } from '../../../data/productCatalog.js';
import { paymentLabel, type AppliedPayment, type PreviewQuote } from '../orderState.js';
import type { EngineStatus } from '../enginePricing.js';

export interface ShippingAddress {
  recipient?: string;
  street: string;
  line2?: string;
  city: string;
  region: string;
  postal: string;
  country: string;
}

export type OrderSummaryMode = 'preview' | 'real';
export type OrderAddressStatus = 'ready' | 'loading' | 'none' | 'error';

export const PRICES_PENDING_LABEL = 'Prices pending the pricing engine';
export const NO_SAVED_CARD_LABEL = 'No saved card on file in this review';
export const LIVE_WRITE_GATE_LABEL = 'Paused by the live-write gate';
export const PENDING_TAX_LABEL = 'Pending Shopify tax';
export const CHANGE_PENDING_LABEL = 'Payment options open once the pricing engine quotes this basket';

/** Example (design-preview) prices keyed by product name, dollars. */
export type ExamplePrices = Record<string, { oneTime: number; subscription: number } | undefined>;

export interface EngineSummary {
  catalogStatus: EngineStatus;
  catalogPrices: Record<string, EngineLine>;
  catalogQuote: EngineQuote | null;
  basketStatus: EngineStatus;
  basketQuote: EngineQuote | null;
  errorCode: string | null;
}

export interface OrderSummaryProps {
  catalog: CatalogEntry[];
  quantities: Record<string, number>;
  subscription: boolean;
  frequencyDays: string;
  address: ShippingAddress;
  addressConfirmed: boolean;
  defaultAddress: boolean;
  payment: AppliedPayment;
  quote: PreviewQuote | null;
  permissionsReady: boolean;
  customer: { initials: string; fullName: string };
  /** 'preview' keeps the approved design output; 'real' renders the honest real-session labels. */
  mode?: OrderSummaryMode;
  /** Real-session address read state; ignored in preview. */
  addressStatus?: OrderAddressStatus;
  /** Design-preview example prices (preview only). */
  examplePrices?: ExamplePrices;
  /** Real-session engine state (real only). */
  engine?: EngineSummary;
  /** Gate the Process order control reports on a real session. */
  draftOrderGate?: string;
  onOpenAddress: () => void;
  onConfirmAddress: () => void;
  onChangePayment: () => void;
  onProcess: () => void;
}

function pendingSuffix(status: EngineStatus, errorCode: string | null): string {
  if (status === 'loading') return 'Loading prices from the pricing engine.';
  if (status === 'error') return `The pricing engine could not be reached (${errorCode ?? 'ERROR'}).`;
  return 'No prices are shown until it is connected.';
}

export function OrderSummary(props: OrderSummaryProps): ReactNode {
  const { catalog, quantities, subscription, frequencyDays, address, addressConfirmed,
    defaultAddress, payment, quote, permissionsReady, customer, mode = 'preview', addressStatus = 'ready',
    examplePrices = {}, engine, draftOrderGate = 'LIVE_WRITE_GATE_SHOPIFY_CREATE_DRAFT_ORDER',
    onOpenAddress, onConfirmAddress, onChangePayment, onProcess } = props;
  const real = mode === 'real';
  const selected = catalog.filter((p) => (quantities[p.name] ?? 0) > 0);
  const hasAddress = [address.street, address.city, address.region, address.postal, address.country].every((v) => v.trim());
  const addressReady = !real || addressStatus === 'ready' || (addressStatus === 'none' && hasAddress);

  // --- Design preview: example table + fixture quote comparator -------
  const exampleUnit = (name: string): number => {
    const entry = examplePrices[name];
    if (!entry) return 0;
    return subscription ? entry.subscription : entry.oneTime;
  };
  const exampleSum = selected.reduce((a, p) => a + (quantities[p.name] ?? 0) * exampleUnit(p.name), 0);

  // --- Real session: the engine's numbers ------------------------------
  const engineReady = !!engine && engine.catalogStatus === 'ready';
  const basketQuote = engine && engine.basketStatus === 'ready' ? engine.basketQuote : null;
  const tierQuote = basketQuote ?? engine?.catalogQuote ?? null;
  const changeEnabled = !real || engineReady;
  const canProcess = !real && selected.length > 0 && hasAddress && addressReady && addressConfirmed && permissionsReady;
  const quoteNote = selected.length === 0 ? 'Choose products to continue.'
    : real && !engineReady ? `${PRICES_PENDING_LABEL}. The order can be priced once it is connected.`
      : !hasAddress || !addressReady ? 'Add a shipping address to continue.'
        : !addressConfirmed ? 'Confirm the shipping address to process the order.'
          : real ? `Final amount awaits tax from Shopify. Draft orders stay paused by the live-write gate (${draftOrderGate}).`
            : !quote ? 'Final amount awaits tax from Shopify.'
              : !permissionsReady ? 'Confirm the customer’s payment permissions using Change before processing the order.'
                : 'Review the final total with the customer.';

  const realLine = (p: CatalogEntry): ReactNode => {
    const qty = quantities[p.name] ?? 0;
    const line = basketQuote?.lineItems.find((l) => l.productHandle === p.handle)
      ?? engine?.catalogPrices[p.handle] ?? null;
    const unit = line && line.unitPrice !== null ? line.unitPrice : null;
    const total = basketQuote && line && line.lineTotal !== null && line.units === qty ? line.lineTotal
      : unit !== null ? unit * qty : null;
    return <div className="basket-line" key={p.handle} data-testid={`basket-line-${p.handle}`}>
      <div><b>{line?.title ?? p.name}</b><small>{qty} × {unit !== null ? formatCents(unit) : 'price pending'}{line?.sku ? ` · ${line.sku}` : ''}</small></div>
      <strong>{total !== null ? formatCents(total) : 'Pending'}</strong>
    </div>;
  };

  const discountLines = (): ReactNode => {
    if (!basketQuote) return null;
    return basketQuote.discounts.map((d) => {
      const isBundle = d.source.startsWith(BUNDLE_DISCOUNT_PREFIX);
      const isSub = d.source === SUBSCRIPTION_DISCOUNT_SOURCE;
      const testId = isBundle ? 'order-discount-bundle' : isSub ? 'order-discount-subscription' : 'order-discount';
      const label = isBundle
        ? `Bundle ${d.sku ?? d.source.slice(BUNDLE_DISCOUNT_PREFIX.length)}${d.units && d.units > 1 ? ` × ${d.units}` : ''}`
        : isSub ? 'Subscription 20% off' : d.label ?? d.source;
      return <div key={d.source} data-testid={testId}>
        <span>{label}</span>
        <span>{d.amount !== null ? `−${formatCents(d.amount)}` : 'applied'}</span>
      </div>;
    });
  };

  return (
    <aside className="card order-summary">
      <div className="card-head"><h3>Your order</h3><span className="small">{subscription ? 'Subscription' : 'One-time'}</span></div>
      <div className="summary-body">
        <div className="summary-customer"><span className="avatar">{customer.initials}</span>
          <div><strong>{customer.fullName}</strong><p>{subscription ? 'Every ' + frequencyDays + ' days' : 'One-time order'} · {real ? 'Draft in this review' : 'Example'}</p></div>
          <span className="mini-tag">Draft</span></div>
        <div id="basket-lines">
          {selected.length === 0 ? <p className="summary-note">Choose a product to start this order.</p>
            : real ? selected.map(realLine)
              : selected.map((p) => {
                const qty = quantities[p.name] ?? 0;
                const unit = exampleUnit(p.name);
                return <div className="basket-line" key={p.handle}>
                  <div><b>{p.name}</b><small>{qty} × {formatCents(Math.round(unit * 100))} · Example</small></div>
                  <strong>{formatCents(Math.round(qty * unit * 100))}</strong>
                </div>;
              })}
        </div>
        {real && !engineReady ? (
          <p className="summary-note" data-testid="order-prices-pending">{PRICES_PENDING_LABEL}. {pendingSuffix(engine?.catalogStatus ?? 'skipped', engine?.errorCode ?? null)}</p>
        ) : null}
        {real && tierQuote ? (
          <p className="summary-note" data-testid="order-tier">{tierLabel(tierQuote.tier)} · {tierSourceLabel(tierQuote)}</p>
        ) : null}
        {real && basketQuote && basketQuote.warnings.some((w) => w.code === 'DEALER_TIER_RETIRED') ? (
          <p className="summary-note" role="alert" data-testid="order-dealer-note">Dealer pricing is handled in QuickBooks. This screen cannot quote it.</p>
        ) : null}
        {real ? (
          <div className="totals">
            {discountLines()}
            <div><span>Products</span><strong data-testid="order-products-total">{basketQuote && basketQuote.subtotal !== null ? formatCents(basketQuote.subtotal) : selected.length === 0 ? formatCents(0) : 'Pending'}</strong></div>
            <div><span>Tax</span><span data-testid="order-tax">{PENDING_TAX_LABEL}</span></div>
          </div>
        ) : (
          <div className="totals"><div><span>Products</span><strong>{formatCents(Math.round(exampleSum * 100))}</strong></div>
            <div><span>Tax</span><span>{quote ? formatCents(quote.taxCents) : 'Awaiting Shopify'}</span></div></div>
        )}
        {real ? (
          <div className="merch-total"><span>Order total</span>
            <strong data-testid="order-total">{PENDING_TAX_LABEL}</strong></div>
        ) : (
          <div className="merch-total"><span>{quote ? 'Order total' : 'Subtotal'}</span>
            <strong>{formatCents(quote ? quote.totalCents : Math.round(exampleSum * 100))}</strong></div>
        )}
        <section className="order-shipping" aria-labelledby="address-title">
          <div className="order-shipping-heading"><Icon id="pin" /><strong id="address-title">Shipping address</strong>
            {defaultAddress ? <span className="order-address-default">{real ? 'HubSpot default' : 'Default'}</span> : null}
            <button type="button" className="order-shipping-edit" onClick={onOpenAddress} disabled={real && addressStatus === 'loading'}>Edit address</button></div>
          {real && addressStatus === 'loading' ? (
            <p className="summary-note" role="status" data-testid="order-address-loading">Loading address…</p>
          ) : real && addressStatus === 'error' && !hasAddress ? (
            <p className="summary-note" role="alert" data-testid="order-address-error">Address could not load from HubSpot. Add one for this order.</p>
          ) : real && !hasAddress ? (
            <p className="summary-note" data-testid="order-address-none">No shipping address on file. Add one to continue.</p>
          ) : (
            <address className="order-shipping-address" data-testid="order-shipping-address">
              {address.recipient || customer.fullName}<br />{address.street}<br />
              {address.line2 ? <>{address.line2}<br /></> : null}
              {address.city}, {address.region} {address.postal}
            </address>
          )}
          <button type="button" className={'order-address-confirm' + (addressConfirmed ? ' confirmed' : '')}
            disabled={!hasAddress || !addressReady || addressConfirmed} aria-pressed={addressConfirmed} onClick={onConfirmAddress}>
            {addressConfirmed ? 'Address confirmed for this order' : 'Confirm address for this order'}
          </button>
        </section>
        <section className="order-payment" aria-labelledby="order-payment-label">
          <div className="order-payment-heading"><strong id="order-payment-label">Payment card</strong>
            <button type="button" className="order-payment-change" aria-haspopup="dialog"
              aria-controls="order-payment-dialog" onClick={onChangePayment} disabled={!changeEnabled}
              aria-describedby={real ? 'order-payment-gate' : undefined}>Change</button></div>
          {real ? (
            <div className="order-payment-method" data-testid="order-no-saved-card">
              <div><strong>{NO_SAVED_CARD_LABEL}</strong><small id="order-payment-gate">
                {LIVE_WRITE_GATE_LABEL}. Card details stay with the payment provider; nothing is charged from this screen.
                {engineReady ? '' : ` ${CHANGE_PENDING_LABEL}.`}
              </small></div></div>
          ) : (
            <div className="order-payment-method"><span className="order-payment-visa">{payment.method === 'new' ? 'CARD' : 'VISA'}</span>
              <div><strong>{paymentLabel(payment)}</strong><small>
                {payment.method === 'new' ? 'Sample entry · No card submitted or verified' : 'Saved card · Expires 08/2028 · Example'}
              </small></div></div>
          )}
        </section>
        {subscription ? <div className="recurring-box"><span>FOLLOWING ORDERS · {real ? 'PRICING ENGINE' : 'EXAMPLE'}</span>
          <p><strong data-testid="order-recurring">{real
            ? (basketQuote && basketQuote.subtotal !== null ? formatCents(basketQuote.subtotal) : 'Pending')
            : formatCents(Math.round(exampleSum * 100))}</strong> every {frequencyDays} days</p><small>Before tax. Final tax comes from Shopify.</small></div> : null}
        <button className="review-quote" type="button" disabled={!canProcess} onClick={onProcess}
          aria-describedby={real ? 'order-process-gate' : undefined}>
          Process order <Icon id="arrow" />
        </button>
        {real ? <p className="summary-note" id="order-process-gate" data-testid="order-process-gate">{LIVE_WRITE_GATE_LABEL} ({draftOrderGate}). No charge, order or subscription is created from this screen.</p> : null}
        <p className="summary-note" role="status">{quoteNote}</p>
      </div>
    </aside>
  );
}
